import React from 'react';

import type { LucideIcon } from 'lucide-react';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
	variant?:
		| 'primary'
		| 'secondary'
		| 'success'
		| 'danger'
		| 'warning'
		| 'info'
		| 'gray';
	size?: 'xs' | 'sm' | 'md' | 'lg';
	icon?: LucideIcon;
	dot?: boolean;
	children: React.ReactNode;
}

const Badge: React.FC<BadgeProps> = ({
	variant = 'gray',
	size = 'sm',
	icon: Icon,
	dot = false,
	className = '',
	children,
	...props
}) => {
	// Base styles using design tokens
	const baseStyles = `
        inline-flex items-center font-medium font-primary
        transition-colors duration-150 ease-in-out
    `
		.trim()
		.replace(/\s+/g, ' ');

	// Variant styles using clean utility classes
	const variantStyles = {
		primary: `bg-primary-100 text-primary-800 border border-primary-200`,
		secondary: `bg-neutral-100 text-neutral-800 border border-neutral-200`,
		success: `bg-success-100 text-success-800 border border-success-200`,
		danger: `bg-error-100 text-error-800 border border-error-200`,
		warning: `bg-warning-100 text-warning-800 border border-warning-200`,
		info: `bg-primary-100 text-primary-800 border border-primary-200`,
		gray: `bg-neutral-100 text-neutral-600 border border-neutral-200`,
	};

	// Size styles using standard Tailwind classes with design token values
	const sizeStyles = {
		xs: `px-2 py-1 text-xs rounded-sm`,
		sm: `px-2 py-1 text-xs rounded-full`,
		md: `px-3 py-1 text-sm rounded-full`,
		lg: `px-3 py-2 text-sm rounded-full`,
	};

	// Icon sizes
	const iconSizes = {
		xs: 'w-2.5 h-2.5',
		sm: 'w-3 h-3',
		md: 'w-3.5 h-3.5',
		lg: 'w-4 h-4',
	};

	// Dot styles
	const dotSizes = {
		xs: 'w-1.5 h-1.5',
		sm: 'w-2 h-2',
		md: 'w-2.5 h-2.5',
		lg: 'w-3 h-3',
	};

	return (
		<span
			className={`
        ${baseStyles}
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `
				.trim()
				.replace(/\s+/g, ' ')}
			{...props}
		>
			{dot && (
				<div
					className={`${dotSizes[size]} rounded-full bg-current mr-1.5 opacity-75`}
				/>
			)}
			{Icon && (
				<Icon className={`${iconSizes[size]} ${children ? 'mr-1' : ''}`} />
			)}
			{children}
		</span>
	);
};

export default Badge;

// Convenience components for common status badges
export const StatusBadge: React.FC<{
	status:
		| 'completed'
		| 'processing'
		| 'failed'
		| 'pending'
		| 'active'
		| 'inactive';
	children?: React.ReactNode;
}> = ({ status, children }) => {
	const statusConfig = {
		completed: { variant: 'success' as const, text: 'Completed' },
		processing: { variant: 'warning' as const, text: 'Processing' },
		failed: { variant: 'danger' as const, text: 'Failed' },
		pending: { variant: 'warning' as const, text: 'Pending' },
		active: { variant: 'success' as const, text: 'Active' },
		inactive: { variant: 'gray' as const, text: 'Inactive' },
	};

	const config = statusConfig[status];

	return (
		<Badge variant={config.variant} dot>
			{children || config.text}
		</Badge>
	);
};
